import React, {
  useRef,
  useEffect,
  useState,
  memo,
  ChangeEvent,
  FormEvent,
} from 'react'
import { Uploader, Message, Input, InputGroup } from 'rsuite'
import { SectionTitle } from './ui_reusable/styles/SectionTitle.styles'
import { StandardButton } from './ui_reusable/styles/Button.styles'
import { PanelSpecialIndent } from './ui_reusable/styles/PlacementMisc.styles'
import useStandardToaster from './ui_reusable/StandardToaster' // Updated import
import { InfoMediumMaxi600Style } from '../styles/Text.styles'
import { VerySmallNormalTag } from './ui_reusable/styles/Tags.styles'
import { StandardInput } from './ui_reusable/StandardInputs'
import CustomIcons from './ui_reusable/CustomIcons'

import EyeCloseIcon from '@rsuite/icons/EyeClose'
import VisibleIcon from '@rsuite/icons/Visible'
import { DataVariablePresentation } from './ui_reusable/styles/DataInfoUtils.styles'
import SimpleAccordion from './ui_reusable/SimpleAccordion'
import { ThreeColumnGrid } from './ui_reusable/styles/PlacementMisc.styles'
import {
  TwoColumnsInPanel,
  Form,
  Button,
  FlexContainer,
  InputWrapper,
  AvatarStylizedContainer,
  ButtonContainerInline,
  PasswordPanel, // Add this import
} from './styles/Profile.styles'
import {
  useAuthUserinfoPartialUpdate,
  getAuthUserinfoRetrieveQueryKey,
} from '../api/generated/endpoints/auth/auth'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../context/AuthContext'

// Types
interface UserData {
  name: string
  email: string
  phone: string
  avatar?: string
  role: string
  client_role?: string
}

interface ValidationErrors {
  old_password?: string[]
  new_password?: string[]
}

interface FieldErrors {
  email: boolean
  phone: boolean
}

interface EditableFieldProps {
  isEditing: boolean
  initialValue: string
  onSave: (value: string) => void
  type?: string
}

// Create a separate EditableField component with local state
const EditableField = memo<EditableFieldProps>(
  ({ isEditing, initialValue, onSave, type = 'text' }) => {
    const [localValue, setLocalValue] = useState(initialValue)

    useEffect(() => {
      setLocalValue(initialValue)
    }, [initialValue, isEditing])

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setLocalValue(e.target.value)
    }

    const handleBlur = () => {
      if (isEditing && localValue !== initialValue) {
        onSave(localValue)
      }
    }

    return (
      <InputWrapper>
        <StandardInput
          type={type}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={!isEditing}
        />
      </InputWrapper>
    )
  },
)

const Profile: React.FC = () => {
  const [userData, setUserData] = useState<UserData | null>(null)
  const [refresh, setRefresh] = useState<number>(new Date().getTime())
  const [isEditingName, setEditName] = useState<boolean>(false)
  const [name, setName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [isEditingPhone, setEditPhone] = useState<boolean>(false)
  const [phone, setPhone] = useState<string>('')

  const { pushToast } = useStandardToaster()
  const { logout } = useAuth()
  const queryClient = useQueryClient()
  const { mutate: updateUserInfo } = useAuthUserinfoPartialUpdate()
  const [uploading, setUploading] = useState<boolean>(false)
  const [uploadKey, setUploadKey] = useState<number>(0)

  const [showChangePassword, setShowChangePassword] = useState<boolean>(false)
  const [oldPassword, setOldPassword] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [error, setError] = useState<boolean>(false)

  const [showOldPassword, setShowOldPassword] = useState<boolean>(false)
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false)

  // Add these new state variables to track original values
  const [originalName, setOriginalName] = useState<string>('')
  const [originalPhone, setOriginalPhone] = useState<string>('')

  const [, setFieldErrors] = useState<FieldErrors>({
    email: false,
    phone: false,
  })

  const phoneInputRef = useRef<(() => void) | null>(null)

  const isValidPhone = (phone: string): boolean => {
    return /^[\d\s+()-]{8,}$/.test(phone)
  }

  useEffect(() => {
    const getUserInfo = async () => {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/userinfo/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        },
      )
      const res = await response.json()
      const data: UserData = res.data

      setUserData(data)
      // Set both current and original values
      setName(data.name)
      setEmail(data.email)
      setPhone(data.phone)

      // Store original values
      setOriginalName(data.name)
      setOriginalPhone(data.phone)
    }

    getUserInfo()
  }, [refresh])

  const hasChanges = (): boolean => {
    return name !== originalName || phone !== originalPhone
  }

  const updateProfile = async () => {
    setFieldErrors({ email: false, phone: false })
    let hasErrors = false

    if (phone && !isValidPhone(phone)) {
      if (phoneInputRef.current) phoneInputRef.current()
      setFieldErrors((prev) => ({ ...prev, phone: true }))
      pushToast(
        <Message type="error">Please enter a valid phone number</Message>,
        { placement: 'topCenter', duration: 5000 },
      )
      hasErrors = true
    }

    if (hasErrors) {
      return
    }

    // Only update if there are actual changes
    if (!hasChanges()) {
      pushToast(<Message type="info">No changes to update</Message>, {
        placement: 'topCenter',
        duration: 5000,
      })

      return
    }

    updateUserInfo(
      {
        data: {
          name: name.trim(),
          phone,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getAuthUserinfoRetrieveQueryKey(),
          })
          setRefresh(new Date().getTime())
          pushToast(
            <Message type="success">Profile updated successfully</Message>,
            { placement: 'topCenter', duration: 5000 },
          )
        },
        onError: () => {
          pushToast(<Message type="error">Failed to update profile</Message>, {
            placement: 'topCenter',
            duration: 5000,
          })
        },
      },
    )
  }

  const handleFileChange = async (files: any[]) => {
    if (!files || !files[0]) return

    setUploading(true)
    const formData = new FormData()

    formData.append('avatar', files[0].blobFile)

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/userinfo/`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        },
      )

      if (response.status === 200) {
        // Invalidate query to update AuthContext
        queryClient.invalidateQueries({
          queryKey: getAuthUserinfoRetrieveQueryKey(),
        })
        setUploadKey((prev) => prev + 1)
        setRefresh(new Date().getTime())
        pushToast(<Message type="success">Uploaded successfully</Message>, {
          placement: 'topCenter',
          duration: 5000,
        })
      } else {
        throw new Error('Upload failed')
      }
    } catch (error) {
      pushToast(<Message type="error">Upload failed</Message>, {
        placement: 'topCenter',
        duration: 5000,
      })
      console.error('Failed to upload avatar:', error)
    } finally {
      setUploading(false)
    }
  }

  const handleUpdatePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setMessage('')
    setError(false)

    const requestData = {
      old_password: oldPassword,
      new_password: newPassword,
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/change_password/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          credentials: 'include',
          body: JSON.stringify(requestData),
        },
      )

      if (response.ok) {
        pushToast(
          <Message type="success">Password updated. Please log in again.</Message>,
          { placement: 'topCenter', duration: 5000 },
        )
        logout()
      } else {
        const errorData = await response.json()

        if (errorData.message.old_password || errorData.message.new_password) {
          setValidationErrors(errorData.message)
        } else {
          setMessage(errorData.message || 'Failed to update password')
          setError(true)
        }
      }
    } catch (err) {
      setMessage('An error occurred. Please try again later.')
      setError(true)
    }
  }

  return (
    <>
      <SectionTitle>Profile settings</SectionTitle>{' '}
      <SimpleAccordion id={'profilePageAccordion'} defaultActiveKey={['0']}>
        <SimpleAccordion.Panel eventKey="0" noCollapse>
          <PanelSpecialIndent>
            {userData && (
              <TwoColumnsInPanel>
                <ThreeColumnGrid>
                  <span css={InfoMediumMaxi600Style}>Name:</span>
                  <EditableField
                    isEditing={isEditingName}
                    initialValue={name}
                    onSave={setName}
                  />
                  <Button
                    onClick={() => {
                      if (isEditingName) {
                        updateProfile()
                      }

                      setEditName(!isEditingName)
                    }}
                  >
                    {!isEditingName ? (
                      <CustomIcons.PenIcon size="xs" />
                    ) : (
                      <CustomIcons.faSave color="var(--icon-color)" />
                    )}
                  </Button>

                  <span css={InfoMediumMaxi600Style}>Email:</span>
                  <EditableField
                    isEditing={false}
                    initialValue={email}
                    onSave={setEmail}
                    type="email"
                  />
                  <span />

                  <span css={InfoMediumMaxi600Style}>Phone:</span>
                  <EditableField
                    isEditing={isEditingPhone}
                    initialValue={phone}
                    onSave={setPhone}
                  />
                  <Button
                    onClick={() => {
                      if (isEditingPhone) {
                        updateProfile()
                      }

                      setEditPhone(!isEditingPhone)
                    }}
                  >
                    {!isEditingPhone ? (
                      <CustomIcons.PenIcon size="xs" />
                    ) : (
                      <CustomIcons.faSave color="var(--icon-color)" />
                    )}
                  </Button>
                  <span css={InfoMediumMaxi600Style}>Roles:</span>

                  <div>
                    {userData?.client_role ? (
                      <VerySmallNormalTag style={{ fontWeight: 600 }}>
                        {userData.client_role.toUpperCase()}
                      </VerySmallNormalTag>
                    ) : (
                      <VerySmallNormalTag style={{ fontWeight: 600 }}>
                        {userData.role.toUpperCase()}
                      </VerySmallNormalTag>
                    )}
                  </div>
                </ThreeColumnGrid>
                <div>
                  <FlexContainer>
                    <AvatarStylizedContainer
                      avatarUrl={userData && userData.avatar}
                    />
                    <Uploader
                      key={uploadKey}
                      action=""
                      autoUpload={false}
                      fileListVisible={false}
                      accept="image/*"
                      onChange={handleFileChange}
                      disabled={uploading}
                      multiple={false}
                    >
                      <StandardButton disabled={uploading}>
                        {'Upload avatar'}
                      </StandardButton>
                    </Uploader>
                  </FlexContainer>
                </div>
              </TwoColumnsInPanel>
            )}
            <div className="mt-20">
              <StandardButton
                onClick={() => setShowChangePassword(!showChangePassword)}
              >
                Change password
              </StandardButton>
              {showChangePassword && (
                <PasswordPanel>
                  {message && (
                    <Message type={error ? 'error' : 'success'}>
                      {message}
                    </Message>
                  )}
                  <Form onSubmit={handleUpdatePassword}>
                    <div className="input-group">
                      <label css={DataVariablePresentation}>Old password</label>
                      <InputGroup inside>
                        <Input
                          name="old_password"
                          size="sm"
                          type={showOldPassword ? 'text' : 'password'}
                          placeholder="Old password"
                          value={oldPassword}
                          onChange={(value: string) => setOldPassword(value)}
                          required
                        />
                        <InputGroup.Button
                          onClick={() => setShowOldPassword(!showOldPassword)}
                        >
                          {showOldPassword ? <VisibleIcon /> : <EyeCloseIcon />}
                        </InputGroup.Button>
                      </InputGroup>
                      {validationErrors.old_password && (
                        <div className="d-block">
                          {validationErrors.old_password[0]}
                        </div>
                      )}
                    </div>
                    <div className="input-group">
                      <label css={DataVariablePresentation}>New password</label>
                      <InputGroup inside>
                        <Input
                          name="new_password"
                          size="sm"
                          type={showNewPassword ? 'text' : 'password'}
                          placeholder="New password"
                          value={newPassword}
                          onChange={(value: string) => setNewPassword(value)}
                          required
                        />
                        <InputGroup.Button
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          {showNewPassword ? <VisibleIcon /> : <EyeCloseIcon />}
                        </InputGroup.Button>
                      </InputGroup>
                      {validationErrors.new_password && (
                        <div className="d-block">
                          {validationErrors.new_password[0]}
                        </div>
                      )}
                    </div>
                    <ButtonContainerInline>
                      <StandardButton type="submit">Submit</StandardButton>
                    </ButtonContainerInline>
                  </Form>
                </PasswordPanel>
              )}
            </div>
          </PanelSpecialIndent>
        </SimpleAccordion.Panel>
      </SimpleAccordion>
    </>
  )
}

export default Profile
